import * as React from "react"
import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import {
  LayoutDashboardIcon,
  UsersIcon,
  FolderIcon,
  MessageCircleIcon,
  ServerIcon,
  BuildingIcon,
  FileTextIcon,
  SquareAsterisk,
  KeyIcon,
  PanelLeftIcon,
  TerminalIcon,
  ShieldAlertIcon,
  SparklesIcon,
  LayoutTemplateIcon,
  BookOpenIcon,
  ClipboardListIcon,
  AlertTriangleIcon,
  ContainerIcon,
  TicketIcon,
  HeartPulseIcon,
  LightbulbIcon,
  GlobeIcon,
  BarChart3Icon,
  ActivityIcon,
  BrainIcon,
  MessageSquareWarningIcon,
  ToggleLeftIcon,
  CpuIcon,
  BotIcon,
} from "lucide-react"

import { NavMain } from "@/components/nav-main"
import { NavUser } from "@/components/nav-user"
import { Logo } from "@/components/logo"
import { Button } from "@/components/ui/button"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"

interface User {
  id: number
  name: string
  email: string
  admin: boolean
  super_admin?: boolean
}

interface AdminSidebarProps extends React.ComponentProps<typeof Sidebar> {
  user: User
}

export function AdminSidebar({ user, ...props }: AdminSidebarProps) {
  const { toggleSidebar } = useSidebar()
  const { t } = useTranslation('common')

  const navMain: Array<{
    title: string
    url?: string
    icon: typeof LayoutDashboardIcon
    activePattern?: string
    children?: Array<{ title: string; url: string; icon?: typeof LayoutDashboardIcon }>
  }> = [
    {
      title: t('sidebar.dashboard'),
      url: "/admin",
      icon: LayoutDashboardIcon,
    },
    {
      title: t('sidebar.features'),
      url: "/admin/features",
      icon: ToggleLeftIcon,
    },
    {
      title: t('sidebar.users'),
      url: "/admin/users",
      icon: UsersIcon,
    },
    {
      title: t('sidebar.organizations'),
      url: "/admin/organizations",
      icon: BuildingIcon,
    },
    {
      title: t('sidebar.projects'),
      url: "/admin/projects",
      icon: FolderIcon,
    },
    {
      title: t('sidebar.managedAgents', { defaultValue: 'Managed Agents' }),
      url: "/admin/managed_agents",
      icon: BotIcon,
    },
    {
      title: t('sidebar.models', { defaultValue: 'Models' }),
      url: "/admin/models",
      icon: CpuIcon,
    },
    {
      title: t('sidebar.messages', { defaultValue: 'Messages' }),
      url: "/admin/messages",
      icon: MessageCircleIcon,
    },
    {
      title: t('sidebar.devInstances'),
      url: "/admin/dev_instances",
      icon: ServerIcon,
    },
    {
      title: t('sidebar.healthChecks', { defaultValue: 'Health Checks' }),
      url: "/admin/dev_instances/health",
      icon: HeartPulseIcon,
    },
    {
      title: t('sidebar.djangoInfra', { defaultValue: 'Django Infra' }),
      url: "/admin/django_infra",
      icon: ContainerIcon,
    },
    {
      title: t('sidebar.domains', { defaultValue: 'Domains' }),
      url: "/admin/domains",
      icon: GlobeIcon,
    },
    {
      title: t('sidebar.templates'),
      url: "/admin/templates",
      icon: LayoutTemplateIcon,
    },
    {
      title: t('sidebar.systemSecrets'),
      url: "/admin/system_secrets",
      icon: SquareAsterisk,
    },
    {
      title: t('sidebar.cayuProvidedSecrets'),
      url: "/admin/cayu_provided_secrets",
      icon: KeyIcon,
    },
    {
      title: t('sidebar.rakeTasks'),
      url: "/admin/rake_tasks",
      icon: TerminalIcon,
    },
    {
      title: t('sidebar.auditLogs'),
      url: "/admin/audits",
      icon: FileTextIcon,
    },
    {
      title: t('sidebar.securityViolations'),
      url: "/admin/security_violations",
      icon: ShieldAlertIcon,
    },
    {
      title: t('sidebar.agentSkills'),
      url: "/admin/agent_skills",
      icon: SparklesIcon,
    },
    {
      title: t('sidebar.accessCodes'),
      url: "/admin/access_codes",
      icon: TicketIcon,
    },
    {
      title: t('sidebar.apiProxyLogs', { defaultValue: 'API Proxy Logs' }),
      url: "/admin/api_proxy_logs",
      icon: ActivityIcon,
    },
    {
      title: t('sidebar.collectiveKnowledge', { defaultValue: 'Collective Knowledge' }),
      icon: BookOpenIcon,
      children: [
        { title: t('sidebar.knowledgeDashboard', { defaultValue: 'Dashboard' }), url: "/admin/collective_knowledge/dashboard", icon: BarChart3Icon },
        { title: t('sidebar.manuals', { defaultValue: 'Manuals' }), url: "/admin/collective_knowledge/manuals", icon: ClipboardListIcon },
        { title: t('sidebar.gotchas', { defaultValue: 'Gotchas' }), url: "/admin/collective_knowledge/gotchas", icon: AlertTriangleIcon },
        { title: t('sidebar.improvements', { defaultValue: 'Improvements' }), url: "/admin/collective_knowledge/improvements", icon: LightbulbIcon },
        { title: t('sidebar.memories', { defaultValue: 'Memories' }), url: "/admin/collective_knowledge/memories", icon: BrainIcon },
      ],
    },
    {
      title: t('sidebar.platformFeedback', { defaultValue: 'Platform Feedback' }),
      url: "/admin/platform_feedbacks",
      icon: MessageSquareWarningIcon,
    },
  ]

  return (
    <Sidebar collapsible="icon" variant="floating" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem className="relative">
            {/* Expanded state: Logo links home */}
            <SidebarMenuButton size="lg" asChild className="hover:bg-transparent active:bg-transparent data-[active=true]:bg-transparent group-data-[collapsible=icon]:hidden">
              <Link href="/">
                <div className="flex flex-1 items-center">
                  <Logo className="h-6" />
                </div>
              </Link>
            </SidebarMenuButton>
            {/* Collapsed state: Logo icon, changes to sidebar icon on hover */}
            <SidebarMenuButton
              size="lg"
              className="hover:bg-accent hidden group-data-[collapsible=icon]:flex justify-center group/logo"
              onClick={toggleSidebar}
              tooltip="Expand sidebar"
            >
              <Logo iconOnly className="h-6 w-6 group-hover/logo:hidden" />
              <PanelLeftIcon className="h-5 w-5 hidden group-hover/logo:block" />
            </SidebarMenuButton>
            {/* Collapse toggle button - only visible when expanded */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-7 w-7 group-data-[collapsible=icon]:hidden"
              onClick={toggleSidebar}
            >
              <PanelLeftIcon className="h-4 w-4" />
              <span className="sr-only">Toggle Sidebar</span>
            </Button>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={navMain} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={user} hideUpgradeButton={true} />
      </SidebarFooter>
    </Sidebar>
  )
}
